import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  EstabelecimentoDataService,
  SetupOperacionalGateService,
  WhatsappConfigDataService,
  WhatsappConfigService,
} from '@nomoh-monorepo/data-access-bardo-administrador';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import {
  BottomSheetComponent,
  DeviceService,
  PhoneInputComponent,
} from '@nomoh-monorepo/shared-common-ui';
import {
  IEstabelecimento,
  IWhatsappConfig,
} from '@nomoh-monorepo/shared-types';
import { StringUtils } from '@nomoh-monorepo/shared-util-tools';
import { isValidPhoneNumber } from 'libphonenumber-js/min';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WhatsappSettingsPanelComponent } from '../whatsapp-settings-panel/whatsapp-settings-panel.component';

@Component({
  selector: 'lib-whatsapp-config',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    DialogModule,
    ProgressSpinnerModule,
    ToggleSwitchModule,
    TooltipModule,
    WhatsappSettingsPanelComponent,
    BottomSheetComponent,
    PhoneInputComponent,
    TranslateModule,
  ],
  templateUrl: './whatsapp-config.component.html',
  styleUrls: ['./whatsapp-config.component.scss'],
  host: {
    'data-tour': 'whatsapp-status',
  },
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0 })),
      ]),
    ]),
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate(
          '400ms ease-out',
          style({ opacity: 1, transform: 'translateY(0)' })
        ),
      ]),
    ]),
  ],
})
export class WhatsappConfigComponent implements OnInit {
  //#region Injeções

  private whatsappConfigService = inject(WhatsappConfigService);
  whatsappConfigDataService = inject(WhatsappConfigDataService);
  estabelecimentoDataService = inject(EstabelecimentoDataService);
  private setupOperacionalGate = inject(SetupOperacionalGateService);
  private notificacaoService = inject(NotificacaoService);
  private deviceService = inject(DeviceService);
  private translate = inject(TranslateService);

  $temZap = computed(
    () => this.estabelecimentoDataService.$temZapIntegration()
  );

  //#endregion

  //#region Signals

  private _testando = signal<boolean>(false);
  $testando = this._testando.asReadonly();
  private _logsExpanded = signal(false);
  private _modalConfirmarRemocaoVisible = signal(false);
  private _internalModalVisible = signal(false);

  private _currentStep = signal(0);
  private _showCodeOption = signal(false);
  private _phoneNumber = signal<string>('');
  private _mostrarEscolhaMetodo = signal<boolean>(
    this.whatsappConfigDataService.$whatsappConectado()
  );

  $logsExpandidos = this._logsExpanded.asReadonly();
  $currentStep = this._currentStep.asReadonly();

  $showCodeOption = this._showCodeOption.asReadonly();
  $phoneNumber = this._phoneNumber.asReadonly();
  $isMobile = this.deviceService.$isMobile;
  $mostrarEscolhaMetodo = this._mostrarEscolhaMetodo.asReadonly();
  $internalModalVisible = this._internalModalVisible; // two-way binding para sincronizar com modal externo

  //#endregion

  //#region  Propriedades

  private fluxoAtendimentoId: string | null = null;
  private webhookUrl: string | undefined;
  private ultimoEstabelecimento: IEstabelecimento | null = null;
  private pendingToggleState: boolean | null = null;
  private setupAoEntrarVerificado = false;

  //#endregion

  //#region ViewChild
  // ViewChild para templates do bottom sheet
  @ViewChild('headerTemplate', { static: false })
  headerTemplate!: TemplateRef<any>;
  @ViewChild('footerTemplate', { static: false })
  footerTemplate!: TemplateRef<any>;
  //#endregion

  //#region Getters para template

  get logsExpandidos(): boolean {
    return this._logsExpanded();
  }

  get modalConfirmacao(): boolean {
    return this._modalConfirmarRemocaoVisible();
  }

  set modalConfirmacao(value: boolean) {
    this._modalConfirmarRemocaoVisible.set(value);
    this._internalModalVisible.set(value);
  }

  get visualStatus(): IWhatsappConfig['status'] {
    return (
      this.whatsappConfigDataService.$whatsappConfig().status ?? 'desconectado'
    );
  }

  /**
   * Determina se pode abrir tela de Pairing Code
   * (permite se estiver fora do cooldown OU se já existir código válido)
   */
  get podeAbrirPairingCode(): boolean {
    return this.whatsappConfigDataService.$qrCodeB64() === undefined;
  }

  //#endregion

  constructor() {
    // Sincronizar modal interno com modal externo
    effect(() => {
      this._internalModalVisible.set(this._modalConfirmarRemocaoVisible());

      if (this.whatsappConfigDataService.$whatsappConectado()) {
        this.ativarWhatsApp();
        this.whatsappConfigService.buscarDadosInstancia();
      }
    });
  }

  ngOnInit(): void {
    const estabelecimento =
      this.estabelecimentoDataService.$estabelecimentoAtual();
    this.ultimoEstabelecimento = estabelecimento ?? null;
    this.fluxoAtendimentoId = this.resolveFluxoAtendimentoId(estabelecimento);

    // Valida se exibe ou não a tab de configuração do WhatsApp (apenas se já tiver configurado ou estiver em processo de configuração)
    if (
      this.whatsappConfigDataService.$qrCodeB64() ||
      this.whatsappConfigDataService.$pairingCodeGenerated()
    ) {
      this._mostrarEscolhaMetodo.set(true);

      if (this.whatsappConfigDataService.$qrCodeB64()) {
        this._currentStep.set(1);
      } else {
        this._currentStep.set(3);
      }
    }

    this.whatsappConfigService.testarConexao(
      this.whatsappConfigDataService.$sessionName()
    );

    void this.verificarSetupAoEntrar();
  }

  private async verificarSetupAoEntrar(): Promise<void> {
    if (this.setupAoEntrarVerificado) {
      return;
    }
    this.setupAoEntrarVerificado = true;

    if (this.whatsappConfigDataService.$whatsappConectado()) {
      return;
    }

    const jaCompleto = await this.setupOperacionalGate.estaCompleto();
    if (jaCompleto) {
      return;
    }

    const concluiu =
      await this.setupOperacionalGate.garantirPrerequisitos('whatsapp');
    if (concluiu) {
      this.abrirEscolhaMetodo();
    }
  }

  //#region Conectar com QrCode
  // Métodos para navegação entre steps
  goToQRCodeStep(): void {
    this._currentStep.set(1);
    this._showCodeOption.set(false);
  }
  /**
   * Usuário escolheu QR Code
   */
  escolherQRCode(): void {
    this._currentStep.set(1);

    // QR code já gerado e válido, pode mostrar direto
    if (this.whatsappConfigDataService.$qrCodeB64()) {
      // Se em cooldown, volta para escolha
      this._currentStep.set(1);
      return;
    }

    // Iniciar conexão via QR Code (timestamp será marcado quando QR chegar)
    void this.whatsappConfigService.iniciarConexao();
  }
  //#endregion

  //#region Conectar com PairCode

  /**
   * Usuário escolheu Pairing Code
   */
  escolherPairingCode(): void {
    // Código não existe ou expirou, ir para tela de digitar telefone
    this._currentStep.set(2);
  }
  showCodeOptionStep(): void {
    this._currentStep.set(2);
    this._showCodeOption.set(true);
  }

  async conectarPorCodigo(): Promise<void> {
    const phoneNumber = this._phoneNumber();
    if (!this.numeroConexaoValido()) {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant('BARDO_WHATSAPP.NOTIFICACOES.NUMERO_INVALIDO')
      );
      return;
    }

    // Validar cooldown antes de gerar código
    if (!this.whatsappConfigDataService.$podeGerarCode()) {
      return;
    }

    // O campo já envia DDI + número local (ex.: 5531999887766 ou 64211234567).
    try {
      this._currentStep.set(3);

      await this.whatsappConfigService.iniciarConexao(phoneNumber);

      this.notificacaoService.notificacaoInfo(
        this.translate.instant('BARDO_WHATSAPP.NOTIFICACOES.AGUARDE_CODIGO')
      );
    } catch (error) {
      console.error('Erro ao conectar por código', error);
      this._currentStep.set(2);
      this.notificacaoService.notificacaoError(
        this.translate.instant('BARDO_WHATSAPP.NOTIFICACOES.ERRO_GERAR_CODIGO')
      );
    }
  }
  numeroConexaoValido(): boolean {
    const phone = this._phoneNumber();
    if (!phone) return false;
    try {
      return isValidPhoneNumber(`+${phone}`);
    } catch {
      return false;
    }
  }

  setPhoneNumber(phone: string | null | undefined): void {
    if (!phone) {
      this._phoneNumber.set('');
      return;
    }
    // Remove tudo que não é número
    const apenasNumeros = phone.replace(/\D/g, '');
    this._phoneNumber.set(apenasNumeros);
  }

  async copiarCodigoConexao(): Promise<void> {
    const codigo = this.whatsappConfigDataService.$pairingCodeGenerated()?.trim();
    if (!codigo) return;

    try {
      await navigator.clipboard.writeText(codigo);
      this.notificacaoService.notificacaoSuccess(
        this.translate.instant('BARDO_WHATSAPP.NOTIFICACOES.CODIGO_COPIADO')
      );
    } catch {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant('BARDO_WHATSAPP.NOTIFICACOES.ERRO_COPIAR')
      );
    }
  }

  //#endregion

  //#region Navegação entre steps
  /**
   * Abre modal/tela de escolha do método de conexão
   */
  abrirEscolhaMetodo(): void {
    this._currentStep.set(0);
    this._mostrarEscolhaMetodo.set(true);
  }
  voltarParaQRCode(): void {
    this._currentStep.set(1);
    this._showCodeOption.set(false);
    this._phoneNumber.set('');
  }

  voltarParaTelefone(): void {
    this._currentStep.set(2);
  }

  /**
   * Volta para tela de escolha do método
   */
  voltarParaEscolha(): void {
    this._currentStep.set(0);
    this._showCodeOption.set(false);
    this._phoneNumber.set('');
  }

  /**
   * Fecha modal/tela de escolha
   */
  fecharEscolhaMetodo(): void {
    this._mostrarEscolhaMetodo.set(false);
    this._currentStep.set(1); // Reset para step padrão
  }

  //#endregion

  //#region Iniciar Pareamento

  async atualizarQRCode(): Promise<void> {
    await this.whatsappConfigService.iniciarConexao();
  }

  async verificarStatus(): Promise<void> {
    this._testando.set(true);

    await this.whatsappConfigService.testarConexao(
      this.whatsappConfigDataService.$sessionName()
    );
    this._testando.set(false);
  }

  //#endregion

  //#region Desconexão e Remoção
  desconectar(): void {
    this._modalConfirmarRemocaoVisible.set(true);
  }

  async confirmarRemocao(): Promise<void> {
    this.limparSessaoTela();

    try {
      await this.executarEncerramento({ removerInstancia: true });
    } catch (error) {
      console.error('Erro ao remover instância do WhatsApp', error);
    }
  }

  limparSessaoTela(): void {
    this._modalConfirmarRemocaoVisible.set(false);
    this._mostrarEscolhaMetodo.set(false);
    this._currentStep.set(0);
  }

  async removerInstancia(): Promise<void> {
    this._modalConfirmarRemocaoVisible.set(true);
  }

  private async executarEncerramento(options?: {
    removerInstancia?: boolean;
  }): Promise<void> {
    this.pendingToggleState = false;

    await this.whatsappConfigService.excluirSessao(
      this.whatsappConfigDataService.$sessionName()
    );

    await this.whatsappConfigService.deleteWhatsappAuth(
      this.fluxoAtendimentoId!
    );

    this._showCodeOption.set(false);
    this.abrirEscolhaMetodo();

    this.whatsappConfigDataService.prepararParaReconexao();
  }

  //#endregion

  //#region  Tratamento de Resultados

  //#endregion

  //#region Funções de exibição e formatação

  private readonly statusConfig = [
    {
      status: 'desconectado',
      Icon: 'pi pi-circle',
      textKey: 'BARDO_WHATSAPP.STATUS.DESCONECTADO_TITULO',
      badgeTextKey: 'BARDO_WHATSAPP.STATUS.DESCONECTADO_BADGE',
      descriptionKey: 'BARDO_WHATSAPP.STATUS.DESCONECTADO_DESC',
      Text: 'WhatsApp Desconectado',
      BadgeText: 'Desconectado',
      Description:
        'Ative o WhatsApp para liberar notificações e fluxos automatizados.',
    },
    {
      status: 'conectando',
      Icon: 'pi pi-spin pi-spinner',
      textKey: 'BARDO_WHATSAPP.STATUS.CONECTANDO_TITULO',
      badgeTextKey: 'BARDO_WHATSAPP.STATUS.CONECTANDO_BADGE',
      descriptionKey: 'BARDO_WHATSAPP.STATUS.CONECTANDO_DESC',
      Text: 'Conectando...',
      BadgeText: 'Conectando',
      Description:
        'Estamos aguardando a leitura do QR Code. Isso pode levar alguns segundos.',
    },
    {
      status: 'conectado',
      Icon: 'pi pi-check-circle',
      textKey: 'BARDO_WHATSAPP.STATUS.CONECTADO_TITULO',
      badgeTextKey: 'BARDO_WHATSAPP.STATUS.CONECTADO_BADGE',
      descriptionKey: 'BARDO_WHATSAPP.STATUS.CONECTADO_DESC',
      Text: 'WhatsApp Conectado',
      BadgeText: 'Conectado',
      Description: 'WhatsApp conectado e funcionando normalmente.',
    },
    {
      status: 'erro',
      Icon: 'pi pi-exclamation-circle',
      textKey: 'BARDO_WHATSAPP.STATUS.ERRO_TITULO',
      badgeTextKey: 'BARDO_WHATSAPP.STATUS.ERRO_BADGE',
      descriptionKey: 'BARDO_WHATSAPP.STATUS.ERRO_DESC',
      Text: 'Erro na Conexão',
      BadgeText: 'Erro',
      Description:
        'Ocorreu um erro na conexão. Verifique a internet e tente novamente.',
    },
    {
      status: 'reconectando',
      Icon: 'pi pi-spin pi-refresh',
      textKey: 'BARDO_WHATSAPP.STATUS.RECONECTANDO_TITULO',
      badgeTextKey: 'BARDO_WHATSAPP.STATUS.RECONECTANDO_BADGE',
      descriptionKey: 'BARDO_WHATSAPP.STATUS.RECONECTANDO_DESC',
      Text: 'Reconectando...',
      BadgeText: 'Reconectando',
      Description: 'Tentando restabelecer a conexão automaticamente...',
    },
    {
      status: 'qrExpirado',
      Icon: 'pi pi-hourglass',
      textKey: 'BARDO_WHATSAPP.STATUS.QR_EXPIRADO_TITULO',
      badgeTextKey: 'BARDO_WHATSAPP.STATUS.QR_EXPIRADO_BADGE',
      descriptionKey: 'BARDO_WHATSAPP.STATUS.QR_EXPIRADO_DESC',
      Text: 'QR Code expirado',
      BadgeText: 'Expirado',
      Description:
        'O QR Code anterior expirou. Clique em "Atualizar QR" ou "Conectar WhatsApp" para gerar um novo código.',
    },
  ];

  mostrarQRCode(): boolean {
    return Boolean(this.whatsappConfigDataService.$qrCodeB64());
  }

  getStatusIcon(): string {
    const config = this.statusConfig.find(
      (s) => s.status === this.visualStatus
    );
    return config?.Icon || 'pi pi-question-circle';
  }

  getStatusText(): string {
    const config = this.statusConfig.find(
      (s) => s.status === this.visualStatus
    );
    return config?.textKey ? this.translate.instant(config.textKey) : 'Status Desconhecido';
  }

  getStatusBadgeText(): string {
    const config = this.statusConfig.find(
      (s) => s.status === this.visualStatus
    );
    return config?.badgeTextKey ? this.translate.instant(config.badgeTextKey) : 'Desconhecido';
  }

  getStatusDescription(): string {
    const config = this.statusConfig.find(
      (s) => s.status === this.visualStatus
    );
    return config?.descriptionKey ? this.translate.instant(config.descriptionKey) : 'Status não identificado';
  }

  get currentStatusConfig() {
    return (
      this.statusConfig.find((s) => s.status === this.visualStatus) || {
        status: 'desconectado',
        Icon: 'pi pi-question-circle',
        textKey: 'BARDO_WHATSAPP.STATUS.DESCONECTADO_TITULO',
        badgeTextKey: 'BARDO_WHATSAPP.STATUS.DESCONECTADO_BADGE',
        descriptionKey: 'BARDO_WHATSAPP.STATUS.DESCONECTADO_DESC',
        Text: 'Status Desconhecido',
        BadgeText: 'Desconhecido',
        Description: 'Status não identificado',
      }
    );
  }
  toggleLogs(): void {
    const expanded = !this._logsExpanded();
    this._logsExpanded.set(expanded);
  }

  getLogIcon(tipo: string): string {
    const icons: Record<string, string> = {
      info: 'pi pi-info-circle',
      success: 'pi pi-check-circle',
      warning: 'pi pi-exclamation-triangle',
      error: 'pi pi-times-circle',
    };
    return icons[tipo] || 'pi pi-circle';
  }

  async ativarWhatsApp(): Promise<void> {
    const ok = await this.setupOperacionalGate.garantirPrerequisitos('whatsapp');
    if (!ok) {
      return;
    }

    this.abrirEscolhaMetodo();
  }
  //#endregion

  //#region Formatação e Utilitários

  formatarTelefone(telefone: string): string {
    return StringUtils.formatarTelefoneComNoveSem55(telefone);
  }

  formatarDataHora(data: Date | string | undefined): string {
    if (!data) return '';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(data));
  }

  formatarHora(data: Date | string | undefined): string {
    if (!data) return '';
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(data));
  }

  private resolveFluxoAtendimentoId(
    estabelecimento?: IEstabelecimento | null
  ): string | null {
    const fluxoIdRaw = estabelecimento?.fluxosAtendimento?.[0]?.id ?? null;
    this.webhookUrl =
      estabelecimento?.fluxosAtendimento?.[0]?.tipoFluxo?.webHookUrl ??
      undefined;
    const fluxoId = fluxoIdRaw ? fluxoIdRaw : null;

    if (!fluxoId) {
      this.notificacaoService.notificacaoWarn(
        this.translate.instant('BARDO_WHATSAPP.NOTIFICACOES.SEM_FLUXO')
      );
    }

    return fluxoId;
  }
  //#endregion

  /**
   * Verifica se existe Pairing Code válido em memória
   */

  async onToggleWhatsapp(event: { checked: boolean }): Promise<void> {
    const habilitado = event.checked;

    if (
      !habilitado &&
      this.whatsappConfigDataService.$whatsappConfig().status === 'conectado'
    ) {
      this._modalConfirmarRemocaoVisible.set(true);
      return;
    }

    //await this.atualizarConfiguracao(habilitado);
  }

  private async atualizarConfiguracao(habilitado: boolean): Promise<void> {
    if (habilitado) {
      // Abrir modal de escolha de método ao invés de iniciar conexão diretamente
      this.abrirEscolhaMetodo();
      return;
    }

    //await this.executarEncerramento();
  }
}
